import YAML from 'yaml'
import type { RenderOptions } from '../assetTypes'
import buildDatasetDataApiScript from './dataApiScript'

const insightsServiceUrl = 'https://api.shared-platform.eidu.com/test-insights'

type DatasetParam = {
  name: string
  defaultValue: string | string[] | null
  type: 'string' | 'integer'
  target: string | null
}

const normalizeDatasetTitle = (rawValue: unknown): string => {
  if (rawValue === null || rawValue === undefined) return ''
  const value = typeof rawValue === 'string' ? rawValue : String(rawValue)
  return value.trim()
}

const resolveFallbackName = (options?: RenderOptions): string => {
  const fields = options?.data?.entity?.fields
  if (!fields) return ''
  const nameField = fields.find((field) => field.name.trim().toLowerCase() === 'name')
  return nameField?.value.trim() ?? ''
}

const normalizeParamType = (rawType: unknown): DatasetParam['type'] => {
  const type = typeof rawType === 'string' ? rawType.trim().toLowerCase() : ''
  return type === 'integer' ? 'integer' : 'string'
}

const normalizeDefaultValue = (rawValue: unknown): string | string[] | null => {
  if (rawValue === null || rawValue === undefined) return null
  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim()
    if (!trimmed) return ''
    const lower = trimmed.toLowerCase()
    if (lower === 'none' || lower === 'null') return null
    return rawValue
  }
  if (Array.isArray(rawValue)) {
    return rawValue.filter((item) => item !== null && item !== undefined).map((item) => String(item))
  }
  return String(rawValue)
}

const normalizeParamTarget = (rawTarget: unknown, fallbackName: string): string | null => {
  if (rawTarget === null) return null
  if (rawTarget === undefined) return fallbackName
  if (typeof rawTarget !== 'string') return fallbackName
  const trimmed = rawTarget.trim()
  return trimmed.length > 0 ? trimmed : fallbackName
}

const parseParams = (raw: unknown): DatasetParam[] => {
  if (!Array.isArray(raw)) return []
  const params: DatasetParam[] = []
  raw.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return
    const record = entry as Record<string, unknown>
    const name = typeof record.name === 'string' ? record.name.trim() : ''
    if (!name) return
    const hasDefault = 'default' in record || 'defaultValue' in record
    const rawDefault = hasDefault ? ('default' in record ? record.default : record.defaultValue) : null
    const defaultValue = normalizeDefaultValue(rawDefault)
    const type = normalizeParamType(record.type)
    const target = normalizeParamTarget(record.target, name)
    params.push({ name, defaultValue, type, target })
  })
  return params
}

const buildDatasetsRenderer = (content: string, options?: RenderOptions) => {
  let parsedQuery = ''
  let parsedParams: DatasetParam[] = []
  let parsedTitle = ''
  let hasExplicitTitle = false
  let parseError = ''
  try {
    const parsed = YAML.parse(content || '') as { query?: unknown; params?: unknown; title?: unknown }
    parsedQuery = parsed?.query ? String(parsed.query).trim() : ''
    parsedParams = parseParams(parsed?.params)
    hasExplicitTitle = !!parsed && Object.prototype.hasOwnProperty.call(parsed, 'title')
    if (hasExplicitTitle) {
      parsedTitle = normalizeDatasetTitle(parsed?.title)
    }
    if (!parsedQuery) parseError = 'YAML must include a non-empty "query" property.'
  } catch (error) {
    parseError = `Failed to parse YAML: ${error instanceof Error ? error.message : String(error)}`
  }

  const fallbackName = resolveFallbackName(options)
  const resolvedTitle = hasExplicitTitle ? parsedTitle : fallbackName

  const serializedDatasetPayload = JSON.stringify({ query: parsedQuery, params: parsedParams })
  const serializedTitle = JSON.stringify(resolvedTitle)
  const serializedParseError = JSON.stringify(parseError)
  const datasetDataApiScript = buildDatasetDataApiScript(insightsServiceUrl)

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      :root {
        color-scheme: light;
      }
      html, body {
        width: 100%;
        height: 100%;
      }
      body {
        margin: 0;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background-color: #ffffff;
        color: #0f172a;
        padding: 12px;
        box-sizing: border-box;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }
      .dataset-title {
        margin: 0 0 8px;
        color: #000000;
        font-family: sans-serif;
        font-size: 13px;
        font-style: normal;
        font-weight: 600;
        line-height: 1.2;
        letter-spacing: normal;
      }
      h3 { margin: 0 0 8px; font-size: 16px; }
      .status {
        font-size: 13px;
        color: #475569;
      }
      #results {
        flex: 1 1 auto;
        min-height: 0;
        overflow: auto;
      }
      #results.number-mode {
        overflow: hidden;
      }
      pre {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        padding: 8px;
        border-radius: 6px;
        overflow: auto;
        max-height: 120px;
        font-family: 'JetBrains Mono', Consolas, monospace;
        font-size: 12px;
        white-space: pre-wrap;
      }
      table {
        border-collapse: collapse;
        width: 100%;
        margin-top: 10px;
      }
      th, td {
        border: 1px solid #e2e8f0;
        padding: 6px 8px;
        font-size: 12px;
        text-align: left;
      }
      th {
        background: #f1f5f9;
      }
      .number-result {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
      }
      .number-value {
        font-size: clamp(40px, 8vw, 96px);
        line-height: 1;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: #0f172a;
        white-space: nowrap;
        word-break: normal;
        overflow-wrap: normal;
      }
      .error {
        color: #b91c1c;
        font-weight: 600;
      }
    </style>
  </head>
  <body>
    <div id="dataset-title" class="dataset-title" style="display:none"></div>
    <div id="results"></div>

    <script>
      const datasetPayload = ${serializedDatasetPayload};
      const baseTitle = ${serializedTitle};
      const renderData = ${JSON.stringify(options?.data ?? null)};
      const auth = renderData && renderData.auth ? renderData.auth : null;
      const parseError = ${serializedParseError};
      const titleEl = document.getElementById('dataset-title');
      const resultsEl = document.getElementById('results');
      let isRunning = false;
      let pendingRun = false;
      let lastParamsKey = '';
      const trellis = window.trellis;

      const escapeText = (value) =>
        String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

      const renderLog = (message) => {
        trellis.log(message);
      };

${datasetDataApiScript}
      const datasetDataApi = createDatasetDataApi({ auth, logMessage: renderLog });

      const renderError = (message) => {
        resultsEl.classList.remove('number-mode');
        resultsEl.innerHTML = '<div class="status error">' + escapeText(message) + '</div>';
      };

      const formatCellValue = (value) => {
        if (value === null || value === undefined) return '';
        if (value instanceof Date) return '';
        if (typeof value === 'bigint') return value.toString();
        const parsedNumeric = parseNumericValue(value);
        if (parsedNumeric) {
          return formatNumberValue(parsedNumeric.value, parsedNumeric.decimals);
        }
        return String(value);
      };

      const renderRows = (rows) => {
        if (!rows || !rows.length) return '<div class="status">No data.</div>';
        const headers = Object.keys(rows[0]);
        const head = '<tr>' + headers.map((h) => '<th>' + escapeText(h) + '</th>').join('') + '</tr>';
        const body = rows
          .map(
            (row) =>
              '<tr>' +
              headers
                .map((h) => '<td>' + escapeText(formatCellValue(row[h])) + '</td>')
                .join('') +
              '</tr>'
          )
          .join('');
        return '<table>' + head + body + '</table>';
      };

      const renderSingleNumber = (value) =>
        '<div class="number-result"><div class="number-value">' + escapeText(value) + '</div></div>';

      const readParamString = (params, paramName) => {
        if (!params || typeof params !== 'object') return '';
        const target = String(paramName || '').trim().toLowerCase();
        if (!target) return '';
        const key = Object.keys(params).find((candidate) => candidate.trim().toLowerCase() === target);
        if (!key) return '';
        const value = params[key];
        if (Array.isArray(value)) {
          const firstValue = value.find((entry) => {
            if (entry === null || entry === undefined) return false;
            return String(entry).trim().length > 0;
          });
          return firstValue === undefined ? '' : String(firstValue).trim();
        }
        if (value === null || value === undefined) return '';
        return String(value).trim();
      };

      const countDecimals = (value) => {
        const stringValue = String(value).trim();
        if (!stringValue) return 0;
        const match = stringValue.match(/^[+-]?(?:\\d+\\.?(\\d*)|\\.(\\d+))$/);
        if (!match) return 0;
        const decimals = match[1] !== undefined ? match[1] : match[2] || '';
        return decimals.length;
      };

      const parseNumericValue = (value) => {
        if (typeof value === 'number') {
          if (!Number.isFinite(value)) return null;
          return { value, decimals: countDecimals(value) };
        }
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (!trimmed) return null;
          if (!/^[+-]?(?:\\d+\\.?\\d*|\\.\\d+)$/.test(trimmed)) return null;
          const numericValue = Number(trimmed);
          if (!Number.isFinite(numericValue)) return null;
          return { value: numericValue, decimals: countDecimals(trimmed) };
        }
        return null;
      };

      const formatNumberValue = (value, decimals) => {
        const safeDecimals = Math.max(0, Math.min(100, Number(decimals) || 0));
        const fixed = Number(value).toFixed(safeDecimals);
        const parts = fixed.split('.');
        const integerPart = parts[0] || '0';
        const decimalPart = parts[1];
        const negative = integerPart.startsWith('-');
        const unsigned = negative ? integerPart.slice(1) : integerPart;
        const withThousands = unsigned.replace(/\\B(?=(\\d{3})+(?!\\d))/g, '.');
        const signedInteger = negative ? '-' + withThousands : withThousands;
        if (!decimalPart) return signedInteger;
        return signedInteger + ',' + decimalPart;
      };

      const toDisplayValue = (value) => {
        if (value === null || value === undefined) return null;
        const parsedNumeric = parseNumericValue(value);
        if (parsedNumeric) {
          return formatNumberValue(parsedNumeric.value, parsedNumeric.decimals);
        }
        if (value instanceof Date) return value.toISOString();
        const textValue = String(value).trim();
        return textValue.length > 0 ? textValue : null;
      };

      const resolveColumnKey = (rows, requestedColumn) => {
        const requested = String(requestedColumn || '').trim();
        if (!requested) return null;
        const requestedLower = requested.toLowerCase();
        const keysByLower = new Map();
        rows.forEach((row) => {
          if (!row || typeof row !== 'object' || Array.isArray(row)) return;
          Object.keys(row).forEach((key) => {
            if (!keysByLower.has(key.toLowerCase())) {
              keysByLower.set(key.toLowerCase(), key);
            }
          });
        });
        if (keysByLower.has(requestedLower)) {
          return keysByLower.get(requestedLower) || null;
        }
        return null;
      };

      const resolveNumberValue = (rows, requestedColumn, requestedType) => {
        const supportedTypes = ['average', 'min', 'max', 'first', 'last'];
        const numberType = String(requestedType || 'average').trim().toLowerCase() || 'average';
        if (!supportedTypes.includes(numberType)) {
          return { error: 'Unsupported number_type "' + requestedType + '". Use: average, min, max, first, last.' };
        }

        if (!Array.isArray(rows) || rows.length === 0) {
          return { error: 'No rows returned for number view.' };
        }

        const columnKey = resolveColumnKey(rows, requestedColumn);
        if (!columnKey) {
          return { error: 'number_column "' + requestedColumn + '" was not found in result rows.' };
        }

        const rawValues = rows.map((row) => {
          if (!row || typeof row !== 'object' || Array.isArray(row)) return undefined;
          return row[columnKey];
        });

        if (numberType === 'first' || numberType === 'last') {
          const orderedValues = numberType === 'first' ? rawValues : [...rawValues].reverse();
          for (const value of orderedValues) {
            const displayValue = toDisplayValue(value);
            if (displayValue !== null) {
              return { value: displayValue };
            }
          }
          return { error: 'Column "' + columnKey + '" has no values for number view.' };
        }

        const numericValues = rawValues
          .map((value) => parseNumericValue(value))
          .filter((entry) => entry !== null);

        if (numericValues.length === 0) {
          return { error: 'Column "' + columnKey + '" has no numeric values for number view.' };
        }

        if (numberType === 'average') {
          let sum = 0;
          let maxDecimals = 0;
          numericValues.forEach((entry) => {
            if (!entry) return;
            sum += entry.value;
            if (entry.decimals > maxDecimals) maxDecimals = entry.decimals;
          });
          const averageValue = sum / numericValues.length;
          return { value: formatNumberValue(averageValue, maxDecimals) };
        }

        const selected = numericValues.reduce((best, current) => {
          if (!best) return current;
          if (numberType === 'min') return current.value < best.value ? current : best;
          return current.value > best.value ? current : best;
        }, null);

        if (!selected) {
          return { error: 'Unable to calculate number value for column "' + columnKey + '".' };
        }

        return { value: formatNumberValue(selected.value, selected.decimals) };
      };

      const getRawProvidedValues = () => {
        const values = trellis.params.get();
        return values && typeof values === 'object' ? values : {};
      };

      const resolveTitleOverride = (provided) => {
        if (!provided || typeof provided !== 'object') return { hasOverride: false, value: '' };
        const titleKey = Object.keys(provided).find((key) => key.trim().toLowerCase() === 'title');
        if (!titleKey) return { hasOverride: false, value: '' };
        const rawValue = provided[titleKey];
        if (Array.isArray(rawValue)) {
          const first = rawValue.find((entry) => entry !== null && entry !== undefined);
          return { hasOverride: true, value: first === undefined ? '' : String(first).trim() };
        }
        if (rawValue === null || rawValue === undefined) return { hasOverride: true, value: '' };
        return { hasOverride: true, value: String(rawValue).trim() };
      };

      const renderTitle = (value) => {
        if (!titleEl) return;
        if (value) {
          titleEl.textContent = value;
          titleEl.style.display = 'block';
          return;
        }
        titleEl.textContent = '';
        titleEl.style.display = 'none';
      };

      const applyTitle = (rawProvided) => {
        const override = resolveTitleOverride(rawProvided);
        renderTitle(override.hasOverride ? override.value : baseTitle);
      };

      const runQuery = async () => {
        if (isRunning) return;
        isRunning = true;
        resultsEl.innerHTML = '';
        try {
          const rawProvided = getRawProvidedValues();
          applyTitle(rawProvided);
          const providedValues = datasetDataApi.normalizeProvidedValues(rawProvided);
          const paramsKey = JSON.stringify(providedValues);
          if (paramsKey === lastParamsKey && !pendingRun) {
            isRunning = false;
            return;
          }
          lastParamsKey = paramsKey;

          const data = await datasetDataApi.loadDatasetRows({ payload: datasetPayload, providedValues });
          const rows = Array.isArray(data && data.rows) ? data.rows : [];
          if (rows.length === 0) {
            resultsEl.classList.remove('number-mode');
            resultsEl.innerHTML = renderRows(rows);
            isRunning = false;
            if (pendingRun) {
              pendingRun = false;
              void runQuery();
            }
            return;
          }
          const numberColumn = readParamString(rawProvided, 'number_column');
          if (numberColumn) {
            const numberType = readParamString(rawProvided, 'number_type');
            const numberValue = resolveNumberValue(rows, numberColumn, numberType || 'average');
            if (numberValue.error) {
              throw new Error(numberValue.error);
            }
            resultsEl.classList.add('number-mode');
            resultsEl.innerHTML = renderSingleNumber(String(numberValue.value ?? ''));
          } else {
            resultsEl.classList.remove('number-mode');
            resultsEl.innerHTML = renderRows(rows);
          }
        } catch (error) {
          const message = error?.message || String(error);
          renderError(message);
        }
        isRunning = false;
        if (pendingRun) {
          pendingRun = false;
          void runQuery();
        }
      };

      if (parseError) {
        applyTitle(getRawProvidedValues());
        renderError(parseError);
        renderLog('Dataset render parse error: ' + parseError);
      } else {
        applyTitle(getRawProvidedValues());
        renderLog('Dataset render ready.');
        const scheduleRun = () => {
          if (isRunning) {
            pendingRun = true;
            return;
          }
          setTimeout(() => {
            void runQuery();
          }, 0);
        };
        trellis.runtime.registerParamsListener((event) => {
          const changedKeys = Array.isArray(event && event.changedKeys) ? event.changedKeys : [];
          if (!event || event.isInitial === true || changedKeys.length > 0) {
            scheduleRun();
          }
        });
      }
    </script>
  </body>
</html>`
}

export default buildDatasetsRenderer

export const collectAssetReferences = () => []
